/**
 * Data definition of a banner and related UI/UX customizations
 */
export interface BannerInfo {
  /** Unique identifier for a banner */
  bannerId: string;

  /** Maps to FluentUI type of of MessageBar
   * @see https://developer.microsoft.com/en-us/fluentui#/controls/web/messagebar#MessageBarType
   */
  bannerType: BannerType;

  /** Strings to render in banner */
  bannerStrings: BannerStrings;

  /** Optional recovery actions you want to render alongside banner message */
  actions?: BannerAction[];

  /**
   * The name of the icon to be used when displaying the command. These icon
   * names correspond to the icons defined in @fluidx/icons.
   *
   * TODO: Provide svg instead of just icon name string, to allow for custom icons.
   */
  icon?: string;

  /**
   * Allow text wrapping of message banner content
   * @default false
   */
  isMultiline?: boolean;

  /**
   * Enable/disable assert and aria alerts on banner
   *
   * @default
   * Default values by BannerType:
   *  - BannerType.error         = true
   *  - BannerType.blocked       = true
   *  - BannerType.severeWarning = false
   *  - BannerType.warning       = false
   *  - BannerType.info          = false
   *  - BannerType.success       = false
   */
  isAssertive?: boolean;

  /**
   * Enable/disable dismiss icon on banner
   *
   * @default
   * Default values by BannerType:
   *  - BannerType.error         = false
   *  - BannerType.blocked       = false
   *  - BannerType.severeWarning = true
   *  - BannerType.warning       = true
   *  - BannerType.info          = true
   *  - BannerType.success       = true
   */
  isDismissible?: boolean;
}

/** Maps to FluentUI type of of MessageBar
 * @see https://developer.microsoft.com/en-us/fluentui#/controls/web/messagebar#MessageBarType
 */
export enum BannerType {
  /** Info styled MessageBar */
  info = 0,
  /** Error styled MessageBar */
  error = 1,
  /** Blocked styled MessageBar */
  blocked = 2,
  /** SevereWarning styled MessageBar */
  severeWarning = 3,
  /** Success styled MessageBar */
  success = 4,
  /** Warning styled MessageBar */
  warning = 5
}

export interface BannerStrings {
  /** Lead, bolded text of the banner */
  bannerTitle: string;

  /** A user friendly error message */
  bannerMessage: string;
}

export interface BannerAction {
  /** Text to render on banner action button */
  label: string;

  /** Callback to execute on banner action click */
  actionCallback: () => void;
}
